/**
 * Customer-facing preventivo share landing.
 *
 * Lives on per-sede domains (verdevercelli.it / verdevalsesia.it / …). The
 * backend builds share links as `https://<sede-domain>/share/preventivo/<token>`
 * (see `core/services/branding.resolve_public_base_url`), so this page is the
 * verdevercelli.it side of that contract.
 *
 * The backend itself lives on avmg.it, but every request from this page goes
 * to a same-origin path (`/api/public/preventivo/...`). The sede's nginx is
 * expected to reverse-proxy `/api/public/preventivo/` → `https://avmg.it/...`
 * server-to-server, so the customer's DevTools never see anything other than
 * the sede's own domain. No cross-origin traffic, no CORS preflight, no
 * `avmg.it` leak in headers / referrer / cookies.
 *
 * No router is mounted — landings/ is a single-page marketing app. App.tsx
 * does a one-shot pathname check and renders this component when the URL
 * matches `^/share/preventivo/<token>/?$`.
 */
import { useEffect, useId, useState } from 'react'

type Info = {
  preventivo_number: string
  title: string
  client_name: string
  grand_total: string
  valid_until: string | null
  expires_at: string
  pdf_url: string
  org_name: string
  org_tagline: string
}

export default function SharePreventivoPage({ token }: { token: string }) {
  const [info, setInfo] = useState<Info | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    // Same-origin call — the sede nginx reverse-proxies this path to the
    // avmg.it backend. The browser sees only verdevercelli.it.
    fetch(`/api/public/preventivo/${token}/info/`, {
      // No cookies needed — the token in the URL IS the auth.
      credentials: 'omit',
    })
      .then(async r => {
        if (cancelled) return
        if (r.status === 410) { setExpired(true); return }
        if (r.status === 404) { setError('Link non valido.'); return }
        if (!r.ok) { setError('Impossibile caricare il preventivo.'); return }
        const data = (await r.json()) as Info
        setInfo(data)
      })
      .catch(() => {
        if (!cancelled) setError('Impossibile caricare il preventivo.')
      })
    return () => { cancelled = true }
  }, [token])

  // Relative PDF URL — same nginx proxy rule that handles the info call
  // forwards `/api/public/preventivo/<token>/pdf/` to the avmg.it backend
  // server-to-server. Customer sees verdevercelli.it for the download too.
  const pdfHref = info ? info.pdf_url : '#'

  const expiresAtLabel = info?.expires_at
    ? new Date(info.expires_at).toLocaleString('it-IT', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })
    : null

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center px-4 py-12 bg-gradient-to-br from-emerald-900 via-[#0a3a26] to-emerald-950">
      {/* Subtle radial vignette deepens the centre so the white card
          and white headline read as crisply as possible regardless of
          the visitor's screen brightness. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)',
        }}
        aria-hidden="true"
      />

      <BotanicalBackdrop />

      <div className="relative z-10 w-full max-w-md">
        {info && (
          <div className="text-center mb-9">
            <p className="text-sm sm:text-base uppercase tracking-[0.3em] font-semibold text-emerald-100">
              {info.org_name}
            </p>
            {info.org_tagline && (
              <p className="mt-1 text-[11px] uppercase tracking-[0.25em] font-medium text-emerald-200/70">
                {info.org_tagline}
              </p>
            )}
            <h1
              className="mt-6 text-4xl sm:text-5xl font-serif font-light leading-tight text-white"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.35)' }}
            >
              La ringraziamo
            </h1>
            <p
              className="mt-1 text-3xl sm:text-4xl font-serif italic font-light text-emerald-200"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.35)' }}
            >
              per averci scelto
            </p>
            <p className="mt-6 text-[15px] max-w-xs mx-auto leading-relaxed text-emerald-50/95">
              Di seguito può scaricare il preventivo per la cura del suo verde.
            </p>
          </div>
        )}

        <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/40">
          {expired && (
            <div className="px-8 py-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-base font-semibold text-gray-800">Link scaduto</p>
              <p className="text-sm text-gray-500 mt-1.5 max-w-xs mx-auto">
                Per ricevere un nuovo link, ci contatti.
              </p>
            </div>
          )}

          {error && !expired && (
            <p className="text-sm text-red-600 text-center px-6 py-12">{error}</p>
          )}

          {!info && !error && !expired && (
            <div className="px-6 py-12 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-emerald-700/30 border-t-emerald-700 rounded-full animate-spin" />
            </div>
          )}

          {info && (
            <div className="p-7">
              <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-700/70 font-bold">
                Preventivo
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900 tracking-tight">{info.preventivo_number}</p>
              <p className="text-sm text-gray-500 mt-0.5">{info.title}</p>

              <div className="mt-6 pt-5 border-t border-dashed border-gray-200 space-y-2.5 text-sm">
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400 text-xs uppercase tracking-wider">Cliente</span>
                  <span className="text-gray-800 font-medium">{info.client_name}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400 text-xs uppercase tracking-wider">Totale</span>
                  <span className="text-emerald-800 font-bold text-base tabular-nums">
                    {Number(info.grand_total).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
                {info.valid_until && (
                  <div className="flex justify-between items-baseline">
                    <span className="text-gray-400 text-xs uppercase tracking-wider">Valido fino al</span>
                    <span className="text-gray-700 tabular-nums">
                      {new Date(info.valid_until).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                )}
              </div>

              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-7 flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl bg-gradient-to-r from-[#013220] to-emerald-700 hover:from-[#016432] hover:to-emerald-600 text-white font-semibold shadow-lg shadow-emerald-900/30 transition-all hover:shadow-xl hover:shadow-emerald-900/40 active:scale-[0.99]"
              >
                <svg className="w-5 h-5 transition-transform group-hover:translate-y-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Scarica il preventivo
              </a>

              {expiresAtLabel && (
                <p className="mt-3 text-[11px] text-gray-400 text-center">
                  Link valido fino al {expiresAtLabel}
                </p>
              )}
            </div>
          )}
        </div>

        {info && (
          <div className="mt-8 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-200/50">
              Powered by Automazione Verde
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Botanical decorations — a small set of hand-drawn SVGs anchored at
// the page edges. Identical to the avmg.it share page so the customer
// gets the same visual whether the link routed to avmg.it or to their
// sede domain. Self-contained: no external SVG assets, no imports.
// ────────────────────────────────────────────────────────────────────

function BotanicalBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <TropicalLeaf className="absolute -top-12 -right-24 w-[22rem] h-[34rem] text-emerald-300 opacity-[0.28] rotate-[18deg]" />
      <WideLeaf className="absolute -bottom-20 -left-16 w-[24rem] h-[24rem] text-emerald-300 opacity-[0.25] rotate-[28deg]" />
      <Daisy className="absolute top-[8%] left-2 sm:left-4 w-28 h-28 sm:w-32 sm:h-32 text-emerald-100 opacity-[0.30] rotate-[12deg]" />
      <Daisy className="hidden md:block absolute top-[72%] right-16 w-24 h-24 text-emerald-100 opacity-[0.30] -rotate-[18deg]" />
      <Daisy className="hidden md:block absolute top-[40%] left-6 w-20 h-20 text-emerald-100 opacity-[0.28] rotate-[24deg]" />
    </div>
  )
}

function TropicalLeaf({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const bodyPath = 'M124 8 C 188 28, 224 100, 224 184 C 222 264, 184 326, 122 350 C 60 326, 22 264, 20 184 C 20 100, 60 28, 124 8 Z'
  return (
    <svg viewBox="0 0 240 360" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`tl-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"  stopColor="currentColor" stopOpacity="0.45" />
          <stop offset="60%" stopColor="currentColor" stopOpacity="0.9"  />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1"   />
        </linearGradient>
        <clipPath id={`tl-clip-${id}`}>
          <path d={bodyPath} />
        </clipPath>
      </defs>
      <path fill={`url(#tl-${id})`} d={bodyPath} />
      <path
        fill="currentColor" fillOpacity="0.18"
        d="M124 36 C 174 54, 202 110, 202 186 C 200 254, 170 304, 122 322 C 74 304, 44 254, 42 186 C 42 110, 74 54, 124 36 Z"
      />
      <g clipPath={`url(#tl-clip-${id})`}>
        <path
          fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="2.2" strokeLinecap="round"
          d="M124 22 C 126 130, 120 240, 122 338"
        />
      </g>
    </svg>
  )
}

function WideLeaf({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const bodyPath = 'M140 14 C 80 22, 32 70, 24 132 C 20 200, 60 264, 140 288 C 220 264, 260 200, 256 132 C 248 70, 200 22, 140 14 Z'
  return (
    <svg viewBox="0 0 280 300" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`wl-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"   stopColor="currentColor" stopOpacity="0.45" />
          <stop offset="60%"  stopColor="currentColor" stopOpacity="0.9"  />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1"    />
        </linearGradient>
        <clipPath id={`wl-clip-${id}`}>
          <path d={bodyPath} />
        </clipPath>
      </defs>
      <path fill={`url(#wl-${id})`} d={bodyPath} />
      <path
        fill="currentColor" fillOpacity="0.18"
        d="M140 42 C 92 48, 54 90, 48 138 C 46 196, 82 248, 140 264 C 198 248, 234 196, 232 138 C 226 90, 188 48, 140 42 Z"
      />
      <g clipPath={`url(#wl-clip-${id})`}>
        <path
          fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="2.2" strokeLinecap="round"
          d="M140 26 C 138 100, 142 200, 140 280"
        />
      </g>
    </svg>
  )
}

function Daisy({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const angles = Array.from({ length: 14 }, (_, i) => i * (360 / 14))
  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`dz-${id}`} x1="50%" y1="100%" x2="50%" y2="0%">
          <stop offset="0%"   stopColor="currentColor" stopOpacity="0.55" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1"    />
        </linearGradient>
      </defs>
      <g fill={`url(#dz-${id})`}>
        {angles.map(a => (
          <path
            key={a}
            transform={`rotate(${a} 100 100)`}
            d="M 100 100 C 94 80, 94 38, 100 18 C 106 38, 106 80, 100 100 Z"
          />
        ))}
      </g>
      <circle cx="100" cy="100" r="14" fill="currentColor" fillOpacity="0.95" />
    </svg>
  )
}
